import React from 'react';
import { TextareaProps as ChakraTextareaProps, Textarea as ChakraTextarea, mergeRefs } from '@chakra-ui/react';
import { FieldValues, Path, useController, useFormContext } from 'react-hook-form';
import { Field, FieldProps } from '../chakra-components';
import { useWatchCallback } from './useWatchCallback';

export interface TextAreaProps<T extends FieldValues, TPath extends Path<T>> extends ChakraTextareaProps {
	ref?: React.Ref<HTMLTextAreaElement>,
	linkTo: TPath,
	disabled?: boolean,
	label?: React.ReactNode,
	hideErrorText?: boolean,
	fieldProps?: FieldProps,
	formatFieldValue?: (value: any) => string,
	unFormatFieldValue?: (value: string) => any,
	onWatch?: (value: any) => void,
}

export type TextAreaRef = HTMLTextAreaElement;

export const TextArea = <T extends FieldValues, TPath extends Path<T>>(props: TextAreaProps<T, TPath>) => {
	const {
		ref,
		linkTo,
		disabled,
		label,
		hideErrorText,
		fieldProps,
		formatFieldValue,
		unFormatFieldValue,
		onWatch,
		...textareaProps
	} = props;
	const { control } = useFormContext<T>();

	// Hook de react-hook-form para manejar el textarea
	const { field, fieldState } = useController({ name: linkTo, control, disabled });
	const invalid = !!fieldState.error;

	useWatchCallback(control, linkTo, onWatch);

	const onFocus = React.useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
		e.target.select();
		textareaProps.onFocus?.(e);
	}, [textareaProps]);

	const onChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {

		const value = e.currentTarget.value;
		const normalized = unFormatFieldValue ? unFormatFieldValue(value) : value;
		field.onChange(normalized);
	}, [field, unFormatFieldValue]);

	return (
		<Field invalid={invalid} label={textareaProps.required ? <><>{label}</> <sup>*</sup></> : label} errorText={hideErrorText ? '' : fieldState.error?.message} {...fieldProps}>
			<ChakraTextarea
				{...field}
				value={formatFieldValue ? formatFieldValue(field.value) : field.value}
				ref={mergeRefs(ref, field.ref)}
				{...textareaProps}
				onFocus={onFocus}
				onChange={onChange}
			/>
		</Field>
	);
};